import { yupResolver } from "@hookform/resolvers/yup";
import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { formConvertErrorMessage, formValidate } from "helpers/form_helper";
import { useCreateRouteCollector } from "hooks/route-collector/useRouteCollector";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Link, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import { Switch } from "antd";
import * as yup from "yup";

const CreateRoute = props => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["routeCollector"]);
  const { addToast } = useToasts();
  const [is_level_2, setIsLevel2] = useState(true); // Mặc định 2 cấp

  const getInitDefaultValues = () => {
    return {
      hub_id: null,
      name: "",
      code: "",
      description: "",
    };
  };

  const schema = yup.object().shape({
    hub_id: yup
      .object()
      .nullable()
      .required(t("form.validate.required", { title: t("form.hub_id") })),
    name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.name") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    code: yup
      .string()
      .required(t("form.validate.required", { title: t("form.code") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    description: yup
      .string()
      .required(t("form.validate.required", { title: t("form.description") })),
  });

  const mainForm = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  const {
    handleSubmit,
    formState: { errors, touchedFields },
    getValues,
    setError,
    register,
    setValue,
  } = mainForm;

  // Xử lý thay đổi địa chỉ 2 cấp
  const handleChangeIsLevel2 = value => {
    setIsLevel2(value);
    setValue("hub_id", null);
  };

  // Create Route
  const onSuccessCreateRoute = _data => {
    navigate("/route-collector/list");
  };
  
  const onErrorCreateRoute = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };

  const { isLoading, mutate: createRoute } = useCreateRouteCollector(
    onSuccessCreateRoute,
    onErrorCreateRoute
  );

  const onSubmit = values => {
    const data = getValues();
    createRoute({
      hub_id: data?.hub_id?.value,
      name: data.name,
      code: data.code,
      description: data.description,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.create")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.create")}
            breadcrumbItems={[
              { name: t("title.manager") },
              { name: t("title.list"), link: "/route-collector/list" },
              { name: t("title.create") },
            ]}
          />

          <Row className="row-cols-1">
            <Col>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col md={6}>
                      <FormGroup>
                        <Label
                          className="d-flex align-items-center gap-1"
                          style={{ cursor: "pointer" }}
                          htmlFor="is_level_2"
                          onClick={() => handleChangeIsLevel2(!is_level_2)}
                        >
                          <Switch
                            checked={is_level_2}
                            onChange={handleChangeIsLevel2}
                          />
                          Địa chỉ 2 cấp
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="row-cols-1 row-cols-md-2">
                    <Col>
                      <FormHubSelect
                        isMyHubOnly
                        isRequired
                        mainForm={mainForm}
                        fieldName="hub_id"
                        t={t}
                        title={t("form.hub_id")}
                        addressLevel={is_level_2 ? 2 : 3}
                        key={`hub-select-${is_level_2}`} 
                      />
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="code">
                          {t("form.code")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "code"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.code"),
                          })}
                          {...register("code")}
                        />
                        {errors.code && (
                          <MessageFormValidate error={errors.code.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="name">
                          {t("form.name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "name"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.name"),
                          })}
                          {...register("name")}
                        />
                        {errors.name && (
                          <MessageFormValidate error={errors.name.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="description">
                          {t("form.description")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "description"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.description"),
                          })}
                          {...register("description")}
                        />
                        {errors.description && (
                          <MessageFormValidate
                            error={errors.description.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="justify-content-end">
                    <Col className="col-auto">
                      <Link
                        to="/route-collector/list"
                        className="btn btn-danger btn-label"
                      >
                        <i className="fas fa-undo label-icon"></i>
                        {t("form.cancel")}
                      </Link>
                    </Col>

                    <Col className="col-auto">
                      <Button
                        color="primary"
                        className="btn-label"
                        type="button"
                        onClick={() => {
                          handleSubmit(onSubmit)();
                        }}
                        disabled={isLoading}
                      >
                        <i className="bx bx-save label-icon"></i>
                        {t("form.save")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateRoute;